/**
 * AddChargeModal Component
 *
 * A modal dialog used to add an additional charge (addon) to a customer's subscription.
 * It allows users to select from fetched addon options and configure quantity and price,
 * depending on the pricing model associated with the addon.
 *
 * Pricing Model Logic:
 * - "flat_fee": Quantity is fixed at 1 and not editable. Price is editable only if not predefined.
 * - "per_unit": Price is fixed and not editable. Quantity is selectable.
 *
 * Props:
 * - country: Used to display the appropriate currency symbol for unit price input.
 * - open: Controls whether the modal is visible.
 * - onClose: Callback to close the modal.
 * - addonOptions: List of addon charge options retrieved from the backend.
 * - dispatch: Dispatch function to update the additional charges state (used for both selecting and saving).
 *
 * Behavior:
 * - Displays dropdowns for item selection and quantity.
 * - Shows input for unit price with logic to enable/disable based on pricing model.
 * - On submission, dispatches actions to add the selected charge and closes the modal.
 */

import React, { CSSProperties, useEffect, useState } from "react";
import {AdditionalChargesState, AdditionalChargesAction, AddonCharge} from "./additionalChargesReducer";
import { countrySpecificCurrency } from "@utils/misc/functions";
import Label from "@components/inputs/text/Label";
import {AddonDetails} from "@services/subscription/SubscriptionService";


interface AddChargeModalProps {
    country: string;
    open: boolean;
    onClose: () => void;
    addonOptions: AddonDetails[];
    dispatch: React.Dispatch<AdditionalChargesAction>;
}

//// Flat-fee, unit pricing, should only show editable quantity if unit.
// Flat fee - shouldn't be able to edit pricing or quantity
// Unit pricing - shouldnt be able to edit price
// Flat fee but blank, should only be able to edit pricing

function AddChargeModal({
    country,
    open,
    onClose,
    addonOptions,
    dispatch,
}: AddChargeModalProps) {
    const [selectedId, setSelectedId] = useState<string>("");
    const [quantity, setQuantity] = useState<number>(1);
    const [unitPrice, setUnitPrice] = useState<number>(0);
    const [selectedPricingModel, setSelectedPricingModel] = useState<string | null>(null);

    useEffect(() => {
        // Updates addon specific state when a new addon is selected
        const selected = addonOptions.find((a) => a.id === selectedId);
        if (selected) {
            setUnitPrice(selected.price ?? 0);
            setSelectedPricingModel(selected.pricingModel ?? null);
            if (selected.pricingModel === "flat_fee" || selected.pricingModel === "Flat Fee") {
                setQuantity(1);
            }
        }
    }, [selectedId]);

    const handleSubmit = () => {
        const selected = addonOptions.find((a) => a.id === selectedId);
        if (!selected) return;

        // Note that this is the conversion from AddonDetails (from subscriptionService)
        // to AddonCharge (reducer and expected by signatureDocumentService)
        dispatch({
            type: "SELECT_ADDON_FROM_LIST",
            payload: {
                addonId: selected.id,
                addonLabel: selected.name,
                addonUnitPrice: unitPrice,
                addonTotalPrice: (unitPrice * quantity).toString(),
                addonQuantity: quantity,
                pricingModel: selected.pricingModel
            } as AddonCharge,
        });

        dispatch({ type: "ADD_SELECTED_ADDON_TO_CHARGES" });

        onClose();
    };

    if (!open) return null;

    const dropDownListStyles: CSSProperties = {
        height: '5vh',
        border: '1px solid #C4C4C4',
        borderRadius: '10px',
        minWidth: '50%',
    };

    // Sort addonOptions alphabetically by name before displaying
    const sortedAddonOptions = [...addonOptions].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.closeButton} onClick={onClose}>
                    ✕
                </div>
                <h3 style={styles.header}>Add charges</h3>

                <div style={styles.field}>
                    <Label testId="item-label" text={"Item"} />
                    <select
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value)}
                        style={{ ...dropDownListStyles, width: "75%" }}
                    >
                        <option value="">Select</option>
                        {sortedAddonOptions.map((addon) => (
                            <option key={addon.id} value={addon.id}>
                                {addon.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <Label testId="quantity-label" text={"Quantity"} />
                    <select
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        disabled={selectedPricingModel === "flat_fee" || selectedPricingModel === "Flat Fee"}
                        style={{
                            ...dropDownListStyles,
                            backgroundColor: (selectedPricingModel === "flat_fee" || selectedPricingModel === "Flat Fee") ? "#f0f0f0" : "white",
                            width: "50%"
                        }}
                    >
                        {[...Array(100)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {i + 1}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <Label
                        testId="price-label"
                        text={`Unit price (${countrySpecificCurrency(country)})`}
                    />
                    <input
                        type="number"
                        value={unitPrice}
                        onChange={(e) => setUnitPrice(Number(e.target.value))}
                        readOnly={selectedPricingModel === "per_unit" || selectedPricingModel === "Per Unit"}
                        style={{
                            ...dropDownListStyles,
                            width: "50%",
                            backgroundColor: (selectedPricingModel === "per_unit" || selectedPricingModel === "Per Unit") ? "#f0f0f0" : "white",
                            cursor: (selectedPricingModel === "per_unit" || selectedPricingModel === "Per Unit") ? "not-allowed" : "auto"
                        }}
                    />
                </div>

                <button style={styles.button} onClick={handleSubmit}>
                    Add charge
                </button>
            </div>
        </div>
    );
};

export default AddChargeModal;


const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    },
    modal: {
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "24px",
        width: "75%",
        // maxWidth: "400px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    header: {
        margin: 0,
        fontWeight: 600,
        fontSize: "18px",
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    button: {
        backgroundColor: "#731963",
        color: "#fff",
        border: "none",
        borderRadius: "20px",
        padding: "10px",
        fontWeight: 600,
        cursor: "pointer",
    },
    closeButton: {
        position: "absolute",
        top: "12px",
        right: "16px",
        fontSize: "18px",
        fontWeight: "bold",
        cursor: "pointer",
        border: "none",
        background: "transparent",
    },
};